import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { formatCurrencyValue } from 'utils/format';

import {
  EARNINGS_AS_OF_PREFIX,
  EARNINGS_DISCLOSURE_TEMPLATE,
  EARNINGS_DOLLARS_PER_K,
  EARNINGS_FUNDED_VOLUME_PREFIX,
  EARNINGS_HERO_LABEL,
} from './earnings.constants';

const formatDate = value =>
  isNil(value) ? null : dayjs(value).format(SHORT_MONTH_DAY_YEAR_FORMAT);

const composeHeroValue = compPaidYtdUsd => {
  if (isNil(compPaidYtdUsd)) {
    return '—';
  }

  return `$${(compPaidYtdUsd / EARNINGS_DOLLARS_PER_K).toFixed(1)}k`;
};

const composePeriodLabel = (start, end) => {
  const startLabel = formatDate(start);
  const endLabel = formatDate(end);

  if (isNil(startLabel) || isNil(endLabel)) {
    return null;
  }

  return `${startLabel} – ${endLabel}`;
};

/**
 * Maps the tpoRecap earnings endpoint into the EARNED pillar historical-record
 * display shape. Returns null when the response is missing (also the 403-degraded
 * path until the owner claim deploys) so the reducer holds its empty state.
 *
 * LEGAL: every figure carries its as-of date + prior-period window. No projection.
 * @param {object|null} data
 * @returns {object|null}
 */
export const mapCommandCenterEarnings = data => {
  if (isNil(data)) {
    return null;
  }

  const deltaVsYtdPrior =
    isNil(data.compPaidYtdUsd) || isNil(data.priorYtdCompUsd)
      ? null
      : data.compPaidYtdUsd - data.priorYtdCompUsd;

  return {
    heroValue: composeHeroValue(data.compPaidYtdUsd),
    heroLabel: EARNINGS_HERO_LABEL,
    fundedCount: data.fundedCount ?? 0,
    priorFundedCount: data.priorYtdFundedCount ?? null,
    fundedVolumeUsd: data.fundedVolumeUsd ?? null,
    fundedVolumeLabel: isNil(data.fundedVolumeUsd)
      ? null
      : `${EARNINGS_FUNDED_VOLUME_PREFIX} ${formatCurrencyValue(data.fundedVolumeUsd)}`,
    deltaVsYtdPrior,
    periodLabel: composePeriodLabel(data.periodStart, data.periodEnd),
    priorPeriodLabel: composePeriodLabel(data.priorPeriodStart, data.priorPeriodEnd),
    asOfDate: formatDate(data.asOfDate),
    asOfLabel: isNil(formatDate(data.asOfDate))
      ? null
      : `${EARNINGS_AS_OF_PREFIX} ${formatDate(data.asOfDate)}`,
    entityName: data.entityName ?? null,
    disclosureText: EARNINGS_DISCLOSURE_TEMPLATE,
  };
};
